import { useEffect, useState } from 'react';
import { NavLink, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import {
  useStore, ThemeControl, Modal, Flash, useFlash, useMediaQuery, COMPACT,
} from './components/ui.jsx';
import {
  IconDashboard, IconFrontDesk, IconSchedule, IconMembers, IconPlans, IconBilling,
  IconSettings,
} from './components/icons.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import ExportData from './components/ExportData.jsx';
import { resetToSeed, staffById, subscribeCorrections } from './data/store.js';
import { backendMode } from './data/backend/mode.js';
import Dashboard from './routes/Dashboard.jsx';
import Members from './routes/Members.jsx';
import MemberDetail from './routes/MemberDetail.jsx';
import Schedule from './routes/Schedule.jsx';
import ClassDetail from './routes/ClassDetail.jsx';
import Plans from './routes/Plans.jsx';
import Billing from './routes/Billing.jsx';
import FrontDesk from './routes/FrontDesk.jsx';

// Short labels because these are tab-bar labels on a phone as well as sidebar
// rows on a desktop, and a tab label that wraps is a broken tab bar.
const NAV = [
  { to: '/', label: 'Dashboard', short: 'Home', Icon: IconDashboard, end: true },
  { to: '/front-desk', label: 'Front desk', short: 'Desk', Icon: IconFrontDesk },
  { to: '/schedule', label: 'Schedule', short: 'Schedule', Icon: IconSchedule },
  { to: '/members', label: 'Members', short: 'Members', Icon: IconMembers },
  { to: '/plans', label: 'Plans', short: 'Plans', Icon: IconPlans },
  { to: '/billing', label: 'Billing', short: 'Billing', Icon: IconBilling },
];

export const ROLE_LABEL = {
  owner: 'Owner',
  admin: 'Admin',
  coach: 'Coach',
  front_desk: 'Front desk',
};

/** Appearance, your data, and whichever escape hatch this mode offers. */
function Settings({ live, me, role, onSignOut, onExport }) {
  return (
    <div className="stack">
      <label className="field">
        <span>Appearance</span>
        <ThemeControl />
      </label>

      <button className="small" onClick={onExport}>Export your data</button>

      {live ? (
        <div className="whoami">
          <div className="who">
            <b>{me?.name ?? 'Signed in'}</b>
            <span>{ROLE_LABEL[role] ?? role ?? ''}</span>
          </div>
          {onSignOut && <button className="small" onClick={onSignOut}>Sign out</button>}
        </div>
      ) : (
        <>
          <p className="hint">Demo data, stored in this browser.</p>
          <button
            className="small"
            onClick={() => { if (confirm('Reset all demo data back to the seed?')) resetToSeed(); }}
          >
            Reset demo data
          </button>
        </>
      )}
    </div>
  );
}

/**
 * onSignOut arrives as a prop rather than being imported, deliberately. This
 * component is rendered in both modes, and importing the auth module here
 * would drag the Supabase SDK back into the demo's entry chunk — the exact
 * 60KB the lazy gate exists to keep out of it.
 */
export default function App({ onSignOut }) {
  const state = useStore();
  const location = useLocation();
  const compact = useMediaQuery(COMPACT);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // The server can disagree with an optimistic guess — the class filled
  // between the click and the request landing. That belongs on screen wherever
  // the operator happens to be, not only on the page that started it.
  const [correction, setCorrection] = useFlash(7000);
  useEffect(() => subscribeCorrections(setCorrection), [setCorrection]);

  const live = backendMode() === 'supabase';
  const me = state.session ? staffById(state.session.staffId) : null;
  const settings = (
    <Settings
      live={live}
      me={me}
      role={state.session?.role}
      onSignOut={onSignOut}
      onExport={() => { setSettingsOpen(false); setExporting(true); }}
    />
  );

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-name">
            <b>FloatDesk</b>
            <span>{state.org.name}</span>
          </div>
          {/* On a phone the footer controls have nowhere to live: the sidebar
              has become a bottom tab bar. They move into a sheet behind this,
              which is where a native app keeps them too. */}
          {compact && (
            <button
              type="button"
              className="icon-button"
              aria-label="Settings"
              onClick={() => setSettingsOpen(true)}
            >
              <IconSettings />
            </button>
          )}
        </div>

        <nav className="nav" aria-label="Sections">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              <span className="glyph"><item.Icon /></span>
              <span className="nav-label">{compact ? item.short : item.label}</span>
            </NavLink>
          ))}
        </nav>

        {!compact && <div className="sidebar-foot">{settings}</div>}
      </aside>

      <main className="main">
        {/* Keyed on pathname so navigating away clears a failed screen. */}
        <ErrorBoundary key={location.pathname}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/front-desk" element={<FrontDesk />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/schedule/:occurrenceId" element={<ClassDetail />} />
            <Route path="/members" element={<Members />} />
            <Route path="/members/:memberId" element={<MemberDetail />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>
      </main>

      {compact && settingsOpen && (
        <Modal title="Settings" onClose={() => setSettingsOpen(false)}>
          {settings}
        </Modal>
      )}

      {exporting && <ExportData onClose={() => setExporting(false)} />}

      <Flash flash={correction} />
    </div>
  );
}
